import { Link } from "react-router-dom";
import { MarketingChrome } from "../components/MarketingChrome";
import { IconDoc, IconLock, IconLogout, IconMic, IconShield, IconWallet } from "../components/Icons";

const CARDS = [
  [IconMic, "Your work", "You record original voice notes using your own voice."],
  [IconWallet, "Payment", "You are paid for recordings that are approved under the applicable contributor rate."],
  [IconShield, "Content quality", "Duplicate, silent, off-topic or unintelligible submissions may not qualify for payment."],
  [IconDoc, "Voice usage", "You grant Inputry the rights described in the Contributor Terms to use, edit, publish and distribute accepted recordings."],
  [IconLock, "Your data", "Contributor information is used for account administration, review and payments according to the Privacy Policy."],
  [IconLogout, "Leaving", "You may stop contributing at any time."],
];

export function TermsPage() {
  return (
    <MarketingChrome back={{ to: "/", label: "Back to Inputry" }}>
      <article className="narrow-page terms-page" style={{ maxWidth: 860 }}>
        <div className="kicker"><i /> Legal</div>
        <h1 className="page-title-xl">Inputry Contributor Terms & Conditions</h1>
        <p className="sub">Effective date: October 1, 2026 · Last updated: October 1, 2026</p>
        <h2>The important parts, in plain English</h2>
        <div className="card-grid">
          {CARDS.map(([Icon, title, copy]) => (
            <article key={String(title)} className="soft-card">
              <div className="icon-tile">
                <Icon width={18} height={18} />
              </div>
              <div className="term-content">
                <h3>{title as string}</h3>
                <p>{copy as string}</p>
              </div>
            </article>
          ))}
        </div>
        <section className="legal-section">
          <h2><span>01</span>About the contributor network</h2>
          <p className="legal-copy">
            Inputry is an invite-only paid voice contributor network. Contributors record short, original
            voice notes to prompts and content clips, and submit them for review. To take part you must
            be at least 18 years old, provide accurate information, and use the invitation sent to the
            email address you applied with.
          </p>
        </section>
        <section className="legal-section">
          <h2><span>02</span>Payment</h2>
          <p className="legal-copy">
            Recordings approved by Inputry earn the contributor rate shown when you create your account.
            Rates may vary by country or project. Payments for approved contributions are made through
            PayPal or Wise once you reach the minimum payout. Duplicate, silent, off-topic or
            unintelligible submissions may not qualify for payment.
          </p>
        </section>
        <section className="legal-section">
          <h2><span>03</span>Content guidelines</h2>
          <p className="legal-copy">Respond naturally and keep it clear. These guidelines help us approve more of your recordings.</p>
          <ul className="legal-copy">
            <li>Respond in your own words and your own voice.</li>
            <li>Follow the prompt.</li>
            <li>Keep recordings within the requested duration.</li>
            <li>Make sure your voice is understandable.</li>
            <li>Natural background noise may be acceptable where permitted.</li>
            <li>Do not submit duplicate recordings.</li>
            <li>Do not submit AI-generated or voice-cloned recordings.</li>
            <li>Do not submit unlawful, threatening, hateful or harassing content.</li>
          </ul>
        </section>
        <section className="legal-section">
          <h2><span>04</span>Voice and content usage rights</h2>
          <p className="legal-copy">
            After you submit a recording and it is accepted, you grant Inputry the rights described in
            this section to use, edit, combine, publish and distribute that recording across Inputry's
            channels. You confirm that the recording is your own voice and original voice note, and that
            you have the right to submit it.
          </p>
        </section>
        <section className="legal-section">
          <h2><span>05</span>Contributor information</h2>
          <p className="legal-copy">
            Inputry uses contributor information for account administration, application and recording
            review, and payments. How we collect, use and protect information is described in the{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </section>
        <section className="legal-section">
          <h2><span>06</span>Ending participation</h2>
          <p className="legal-copy">
            You may stop contributing at any time. Payment obligations for qualifying, previously
            accepted work remain governed by these Contributor Terms.
          </p>
        </section>
        <section className="legal-section">
          <h2><span>07</span>Additional legal terms</h2>
          <p className="legal-copy">
            We may update these terms from time to time. If we make material changes, we will let
            contributors know before the changes apply to new submissions.
          </p>
        </section>
        <section className="legal-section">
          <h2><span>08</span>Contact</h2>
          <p className="legal-copy">
            Questions about these terms? Reach us through the <Link to="/contact">Contact page</Link> and
            our team will get back to you.
          </p>
        </section>
      </article>
    </MarketingChrome>
  );
}
